import React, { useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp, FaFileExcel, FaCopy, FaDownload, FaImage } from 'react-icons/fa';
import { C, detailSectionCard } from '../utils/constants';
import { downloadSvgAsPng, copySvgAsPng } from '../utils/exports';
import { UNIT_LABELS } from '../utils/helpers';

/* ── Bouton export générique ────────────────────────────────────────────────── */
export const XBtn = ({ onClick, color, bg, border, title, children }) => (
  <button onClick={onClick} title={title} style={{
    background: bg, color, border: `1px solid ${border}`,
    borderRadius: 6, padding: '4px 10px', fontSize: '0.70rem',
    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.5,
    display: 'inline-flex', alignItems: 'center', gap: 4,
    transition: 'filter 0.15s',
  }}
  onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.93)'}
  onMouseLeave={e => e.currentTarget.style.filter = 'none'}
  >{children}</button>
);

/* ── Barre export Excel + Copier ────────────────────────────────────────────── */
export const TableExportBar = ({ onExcel, onCopy, style }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 5, ...style }}>
    {onExcel && <XBtn onClick={onExcel} color="#2e6b30" bg="#eaf4ea" border="#b2d9b4"><FaFileExcel size={11} /> Excel</XBtn>}
    {onCopy  && <XBtn onClick={onCopy}  color="#1a5296" bg="#e6f0fb" border="#9ab8e0"><FaCopy size={10} /> Copier</XBtn>}
  </div>
);

/* ── Wrapper export SVG → PNG ───────────────────────────────────────────────── */
export const SvgExportBox = ({ children, filename, style }) => {
  const ref = useRef(null);
  const getSvg = () => ref.current?.querySelector('svg');
  return (
    <div style={style}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 5, marginBottom: 4 }}>
        <XBtn onClick={() => { const s = getSvg(); if (s) downloadSvgAsPng(s, filename); }}
          color="#5a3e7a" bg="#f3eef9" border="#c9b4e0" title="Télécharger en PNG"><FaDownload size={10} /> PNG</XBtn>
        <XBtn onClick={() => { const s = getSvg(); if (s) copySvgAsPng(s); }}
          color="#1a5296" bg="#e6f0fb" border="#9ab8e0" title="Copier en PNG"><FaImage size={10} /> PNG</XBtn>
      </div>
      <div ref={ref}>{children}</div>
    </div>
  );
};

/* ── Header tri pour colonnes ───────────────────────────────────────────────── */
export const ThSort = ({ label, field, sortKey, asc, onSort, right }) => (
  <th
    onClick={() => onSort(field)}
    style={{
      padding: '9px 12px', cursor: 'pointer', userSelect: 'none',
      textAlign: right ? 'right' : 'left', whiteSpace: 'nowrap',
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: 0.5, color: sortKey === field ? '#6d4e36' : '#888',
      background: '#fdf8f4', borderBottom: '2px solid #e8ddd4',
    }}
  >
    {label}
    <span style={{ marginLeft: 4, fontSize: '0.58rem', opacity: sortKey === field ? 1 : 0.22 }}>
      {sortKey === field ? (asc ? '▲' : '▼') : '⇅'}
    </span>
  </th>
);

/* ── Pagination ─────────────────────────────────────────────────────────────── */
export const DetailPager = ({ page, setPage, totalPages, total, PAGE_SIZE = 20 }) => {
  if (total <= PAGE_SIZE) return null;
  const from = (page - 1) * PAGE_SIZE + 1;
  const to   = Math.min(page * PAGE_SIZE, total);
  const btn = (disabled, active) => ({
    minWidth: 26, height: 24, border: `1px solid ${active ? '#6d4e36' : '#e0d8d0'}`,
    borderRadius: 5, background: active ? '#6d4e36' : disabled ? '#f7f5f2' : '#fff',
    color: active ? '#fff' : disabled ? '#ccc' : '#6d4e36',
    cursor: disabled ? 'default' : 'pointer', fontSize: '0.72rem', padding: '0 4px',
    fontWeight: active ? 700 : 400,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid #f0ebe4', flexWrap: 'wrap', gap: 6 }}>
      <span style={{ fontSize: '0.72rem', color: '#aaa' }}>
        <span style={{ color: '#6d4e36', fontWeight: 600 }}>{from}–{to}</span> sur {total}
      </span>
      <div style={{ display: 'flex', gap: 3 }}>
        <button onClick={() => setPage(1)}          disabled={page === 1}          style={btn(page === 1, false)}>«</button>
        <button onClick={() => setPage(p => p - 1)} disabled={page === 1}          style={btn(page === 1, false)}>‹</button>
        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
          const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
          return p <= totalPages
            ? <button key={p} onClick={() => setPage(p)} style={btn(false, p === page)}>{p}</button>
            : null;
        })}
        <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} style={btn(page === totalPages, false)}>›</button>
        <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={btn(page === totalPages, false)}>»</button>
      </div>
    </div>
  );
};

/* ── Header section détail (dégradé marron) ─────────────────────────────────── */
export const DetailSectionHeader = ({ title, icon, color, count, loading, onExport, onCopy, open, setOpen }) => (
  <div
    onClick={() => setOpen(o => !o)}
    style={{
      background: 'linear-gradient(135deg, #6d4e36 0%, #8b5e3c 100%)',
      color: '#fff', padding: '10px 18px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      cursor: 'pointer', userSelect: 'none',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.88rem' }}>
      <span style={{ color: color || '#f5c59a' }}>{icon}</span>
      {title}
      {!loading && (
        <span style={{ fontSize: '0.68rem', background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '1px 8px', fontWeight: 500 }}>
          {count} ligne{count !== 1 ? 's' : ''}
        </span>
      )}
      {loading && <Spinner size="sm" style={{ marginLeft: 4 }} />}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {onExport && !loading && count > 0 && (
        <button onClick={e => { e.stopPropagation(); onExport(); }}
          style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 5, padding: '2px 10px', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}>
          ↓ Excel
        </button>
      )}
      {onCopy && !loading && count > 0 && (
        <button onClick={e => { e.stopPropagation(); onCopy(); }}
          style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 5, padding: '2px 10px', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}>
          ⎘ Copier
        </button>
      )}
      {open ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
    </div>
  </div>
);

/* ── Sélecteur d'unité monétaire par section — persisté en localStorage via d.setUnit ── */
export const UnitPicker = ({ unit, onChange }) => (
  <div style={{ display: 'flex', gap: 2 }} onClick={e => e.stopPropagation()}>
    {Object.entries(UNIT_LABELS).map(([val, label]) => (
      <button key={val} onClick={() => onChange(val)} style={{
        padding: '2px 7px', fontSize: '0.6rem', fontWeight: 700,
        border: `1px solid ${unit === val ? '#4a505c' : '#d0c8bc'}`,
        background: unit === val ? '#4a505c' : '#f7f5f2',
        color: unit === val ? '#fff' : '#999',
        borderRadius: 4, cursor: 'pointer', lineHeight: 1.6,
      }}>{label}</button>
    ))}
  </div>
);

/* ── Wrapper section collapsible + drag & drop ──────────────────────────────── */
export const SectionWrapper = ({ id, title, collapsed, onToggle, dragging, dragOver, onDragStart, onDragOver, onDrop, onDragEnd, filterPills, onScrollToFilters, unitPicker, children }) => (
  <div
    onDragOver={e => { e.preventDefault(); e.stopPropagation(); onDragOver(id); }}
    onDrop={e => { e.preventDefault(); e.stopPropagation(); onDrop(id); }}
    style={{
      marginTop: 14,
      opacity: dragging === id ? 0.45 : 1,
      outline: dragOver === id && dragging !== id ? `2px dashed ${C.primary}` : '2px solid transparent',
      borderRadius: 10,
      transition: 'opacity 0.15s, outline 0.1s',
    }}
  >
    <div
      draggable
      onDragStart={e => { e.stopPropagation(); onDragStart(id); }}
      onDragEnd={onDragEnd}
      onClick={() => onToggle(id)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px', background: '#f5f4f2',
        borderRadius: collapsed ? 8 : '8px 8px 0 0',
        border: '1px solid #d4c8bc', userSelect: 'none', cursor: 'pointer',
        minHeight: 44,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ cursor: 'grab', color: '#b0a898', fontSize: '1.1rem', lineHeight: 1, padding: '4px 2px', touchAction: 'none' }} title="Déplacer la section">⠿</span>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#4a505c', textTransform: 'uppercase', letterSpacing: 0.6 }}>{title}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {unitPicker}
        <span style={{ fontSize: '0.75rem', color: '#999', fontWeight: 600, padding: '4px 6px' }}>{collapsed ? '▼' : '▲'}</span>
      </div>
    </div>
    {!collapsed && filterPills && filterPills.length > 0 && (
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, padding: '6px 12px', background: '#faf9f7', borderLeft: '1px solid #d4c8bc', borderRight: '1px solid #d4c8bc' }}>
        {filterPills.map(p => (
          <span key={p.key} style={{ fontSize: '0.65rem', background: '#f0ede8', color: '#6b6058', borderRadius: 10, padding: '2px 8px', border: '1px solid #d4c8bc', whiteSpace: 'nowrap' }}>{p.label}</span>
        ))}
        <button onClick={e => { e.stopPropagation(); onScrollToFilters?.(); }}
          style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#666', background: '#fff', border: '1px solid #d4c8bc', borderRadius: 10, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, minHeight: 30 }}>
          ↑ Filtres
        </button>
      </div>
    )}
    {!collapsed && children}
  </div>
);
